import { useMutation, useQuery } from "@tanstack/react-query";
import { formConvertErrorMessage } from "helpers/form_helper";
import { errorMessage } from "helpers/utils";
import React from "react";
import { useTranslation } from "react-i18next";
import { useToasts } from "react-toast-notifications";

/**
 * @template [TSuccess = any]
 * @typedef {{
 *  translation: string[];
 *  target: string;
 *  queryKey: any[];
 *  queryFn: () => any;
 * } & any} UseListCommonProps
 */

/**
 * @template [TSuccess = any]
 * @typedef {{
 *  translation: string[];
 *  target: string;
 *  queryKey: any[];
 *  queryFn: () => any;
 * } & any & {
 *  selectResponse?: (data: any) => any;
 * }} UseSelectCommonProps
 */

/**
 * @template [TVariables = any]
 * @typedef {{
 *  translation: string[];
 *  translationAction?: string;
 *  target: string;
 *  mutationFn: import("@tanstack/react-query").MutationFunction<import("axios").AxiosResponse<any, any>, TVariables>;
 *  isNotifySuccess?: boolean;
 *  isNotifyError?: boolean;
 * } & any} UseMutationCommonProps
 */
//  mutationFn: (val: TVariables) => any;

/**
 * @typedef {{
 *  data: {
 *    items: any[];
 *    _links?: Record<string, any>;
 *    _meta?: {
 *      currentPage?: number;
 *      pageCount?: number;
 *      perPage?: number;
 *      totalCount?: number;
 *    };
 *  };
 * }} ListResponseProps
 */

/**
 * List Common
 * @template T
 * @param {UseListCommonProps<T>} val
 */
export const useQueryCommon = ({
  payload,
  onSuccess,
  onError,
  translation = "common",
  target,
  queryKey,
  queryFn,
}) => {
  const { t } = useTranslation(translation);
  const { addToast } = useToasts();

  const {
    params,
    key,
    isEnabled,
    staleTime,
    cacheTime,
    refetchOnMount,
    refetchOnWindowFocus,
  } = payload;

  const query = useQuery({
    queryKey: [...queryKey],
    queryFn,
    enabled: isEnabled !== undefined ? isEnabled : true,
    retry: 1,
    staleTime: staleTime !== undefined ? staleTime : 5 * 60 * 1000, // 5 minutes
    cacheTime: cacheTime !== undefined ? cacheTime : 5 * 60 * 1000, // 5 minutes
    refetchOnMount: refetchOnMount !== undefined ? refetchOnMount : false,
    refetchOnWindowFocus:
      refetchOnWindowFocus !== undefined ? refetchOnWindowFocus : false,
    onSuccess,
    onError: (/** @type {any} */ error) => {
      const errorResponse = errorMessage(error);

      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.list", {
              target: t(target),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );

      const errorData = error?.response?.data;

      if (errorData.code === 422 && errorData.fields.length > 0) {
        errorData.fields.forEach(item => {
          switch (item.field) {
            default:
              addToast(
                <>
                  <div className="fs-4">{item.field}</div>
                  <p>{item.message}</p>
                </>,
                {
                  appearance: "error",
                  autoDismiss: true,
                }
              );
              break;
          }
        });
      }

      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
    /**
     * @param {ListResponseProps} response
     */
    select: response => {
      const { data } = response;
      return {
        items: data?.items || data || [],
        params,
        totalCount: data?._meta?.totalCount || data?.items?.length || 0,
        meta: data?._meta,
      };
    },
  });

  return { query };
};

/**
 * Mutation Common
 * @template TVariables
 * @template TSuccess
 * @param {UseMutationCommonProps<TVariables>} val
 */
export const useMutationCommon = ({
  onSuccess,
  onError,
  mutationFn,
  target,
  translation,
  translationAction,
  isNotifyError = true,
  isNotifySuccess = true,
}) => {
  const { t } = useTranslation(translation);
  const { addToast } = useToasts();

  const mutation = useMutation({
    mutationFn,
    retry: 1,
    /**
     * @param {{
     *  data: TSuccess
     * }} response
     * @param {TVariables} variables
     */
    onSuccess: (response, variables) => {
      const { data } = response;

      if (isNotifySuccess) {
        addToast(
          t(`message.${translationAction}`, {
            target: t(target),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
      }

      if (typeof onSuccess === "function") {
        onSuccess({ data: data || response || {}, variables });
      }
    },
    onError: (/** @type {any} */ error) => {
      const errorResponse = errorMessage(error);

      if (isNotifyError) {
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t(`message.${translationAction}`, {
                target: t(target),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      }

      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });

  return { mutation };
};
